import { MEDICATION_CODE_SYSTEMS } from '@liza/fhir-utils';
import type {
  Coding,
  Condition,
  DataRequirement,
  Extension,
  MedicationRequest,
  Observation,
  Patient,
  Period,
  PlanDefinition,
  PlanDefinitionAction,
  Resource,
  ValueSet,
} from '@medplum/fhirtypes';
import { codingSatisfies } from '../matching';
import { differenceInYears, parseISO } from 'date-fns';
import { leafActions } from './planDefinition';
import { valueSetRxcuis } from '../valueSets/rxNormDrugClasses';
import type { ContextQuery, SearchFn } from './types';

/** Results of the context queries, keyed by query name. */
export type ContextResources = Record<string, Resource[]>;

/** The one query every protocol needs. */
export const patientQuery: ContextQuery = {
  name: 'patient',
  resourceType: 'Patient',
  params: (patientRef) => ({ _id: patientRef.replace(/^Patient\//, '') }),
};

/**
 * Every chart query is scoped by `_compartment`, not `patient`. An import files its records under a
 * linked child Patient — the shadow record a Metriport or Fasten export creates — whose subject is
 * that child, so `patient=Patient/<primary>` never sees them; the child's records carry the primary
 * Patient as an account, which puts them in the primary's compartment. The chart's own list reads
 * scope the same way, so what the protocol anchors on is what the chart shows.
 */
const inCompartment = (patientRef: string): Record<string, string> => ({ _compartment: patientRef });

export const activeMedicationRequestsQuery: ContextQuery = {
  name: 'activeMedicationRequests',
  resourceType: 'MedicationRequest',
  params: (patientRef) => ({ ...inCompartment(patientRef), status: 'active', _count: '100' }),
};

export const activeConditionsQuery: ContextQuery = {
  name: 'activeConditions',
  resourceType: 'Condition',
  params: (patientRef) => ({
    ...inCompartment(patientRef),
    'clinical-status': 'active,recurrence,relapse',
    _count: '100',
  }),
};

/** Results key for the latest-Observation query of one coding — namespaced by system, so two systems sharing a code string cannot collide. */
export const evidenceKey = (system: string, code: string): string => `latest:${system}|${code}`;

/** Latest Observation for one code, from any source — the evidence that anchors a cadence. */
export const latestObservationQuery = (system: string, code: string): ContextQuery => ({
  name: evidenceKey(system, code),
  resourceType: 'Observation',
  params: (patientRef) => ({ ...inCompartment(patientRef), code: `${system}|${code}`, _sort: '-date', _count: '1' }),
});

/**
 * The search parameter that matches a resource type's code, for an evidence requirement that names
 * codes without saying how to search them. A requirement may override it with `codeFilter.searchParam`.
 */
const EVIDENCE_CODE_PARAM: Partial<Record<Resource['resourceType'], string>> = {
  Observation: 'code',
  Procedure: 'code',
  DiagnosticReport: 'code',
  Immunization: 'vaccine-code',
  Condition: 'code',
};

/** Results key for one of an action's evidence requirements. */
export const evidenceRequirementKey = (actionId: string, index: number): string => `evidence:${actionId}:${index}`;

/**
 * The search one `input` requirement asks for: the newest resource of its type carrying any of the
 * codes it names. Undefined when the requirement names no code — a requirement with nothing to match
 * would return the whole history and anchor a cadence on an unrelated row.
 */
export const evidenceRequirementQuery = (
  actionId: string,
  index: number,
  requirement: DataRequirement,
): ContextQuery | undefined => {
  const type = requirement.type as Resource['resourceType'] | undefined;
  const filter = requirement.codeFilter?.[0];
  const param = filter?.searchParam ?? (type ? EVIDENCE_CODE_PARAM[type] : undefined);
  const tokens = (filter?.code ?? [])
    .filter((c): c is Coding & { code: string } => !!c.code)
    .map((c) => (c.system ? `${c.system}|${c.code}` : c.code));
  if (!type || !param || tokens.length === 0) return undefined;
  return {
    name: evidenceRequirementKey(actionId, index),
    resourceType: type,
    params: (patientRef) => ({ ...inCompartment(patientRef), [param]: tokens.join(','), _sort: '-date', _count: '1' }),
  };
};

/**
 * Every piece of evidence a protocol's actions anchor on gets a latest-resource query, so the planner
 * can anchor each cadence on what was actually done rather than on when the protocol was applied.
 *
 * An action says what satisfies it in one of two ways. An `input` (a FHIR `DataRequirement`) names a
 * resource type and the codes that identify the study on it — a colonoscopy is a `Procedure` or a
 * `DiagnosticReport`, a flu shot an `Immunization` — and an action may carry several, one per type.
 * An action with no `input` but a `code` is a measurement: its evidence is the latest Observation
 * carrying that code.
 */
export const evidenceQueriesFor = (planDefinition: PlanDefinition): ContextQuery[] => {
  const seen = new Set<string>();
  const queries: ContextQuery[] = [];
  const add = (query: ContextQuery | undefined): void => {
    if (!query || seen.has(query.name)) return;
    seen.add(query.name);
    queries.push(query);
  };
  for (const action of leafActions(planDefinition)) {
    if (action.input?.length && action.id) {
      action.input.forEach((requirement, index) => add(evidenceRequirementQuery(action.id!, index, requirement)));
      continue;
    }
    const coding = action.code?.[0]?.coding?.[0];
    if (!coding?.code || !coding.system) continue;
    add(latestObservationQuery(coding.system, coding.code));
  }
  return queries;
};

export const loadContextResources = async (
  search: SearchFn,
  patientRef: string,
  queries: ContextQuery[],
): Promise<ContextResources> => {
  const entries = await Promise.all(
    queries.map(async (q) => {
      const found = await search(q.resourceType, q.params(patientRef));
      // A search is answered by the server, but the results are keyed by the query's own name and read
      // by helpers that assume the type that query asked for. Keeping only matching resources makes
      // `resources.activeConditions` hold Conditions and nothing else, so readers cast rather than filter.
      return [q.name, found.filter((r) => r.resourceType === q.resourceType)] as const;
    }),
  );
  return Object.fromEntries(entries);
};

// --- derivations shared by protocols -------------------------------------------------------------------

export const patientOf = (resources: ContextResources): Patient | undefined =>
  resources.patient?.[0] as Patient | undefined;

export const latestObservation = (resources: ContextResources, system: string, code: string): Observation | undefined =>
  resources[evidenceKey(system, code)]?.[0] as Observation | undefined;

/** The instant an Observation is about, for anchoring: `effectiveDateTime`, else the period start, else `issued`. */
export const observationInstant = (observation: Observation | undefined): string | undefined =>
  observation?.effectiveDateTime ?? observation?.effectivePeriod?.start ?? observation?.issued;

/**
 * The instant a piece of evidence is about — when the study was done — for anchoring a cadence on it.
 * Each resource type keeps that in its own field; a type this does not know anchors nothing.
 */
export const resourceInstant = (resource: Resource | undefined): string | undefined => {
  switch (resource?.resourceType) {
    case 'Observation':
      return observationInstant(resource);
    case 'Procedure':
      return resource.performedDateTime ?? resource.performedPeriod?.start ?? resource.performedPeriod?.end;
    case 'DiagnosticReport':
      return resource.effectiveDateTime ?? resource.effectivePeriod?.start ?? resource.issued;
    case 'Immunization':
      return resource.occurrenceDateTime;
    case 'Condition':
      return resource.onsetDateTime ?? resource.recordedDate;
    default:
      return undefined;
  }
};

/**
 * The newest evidence an action's `input` requirements found, across every type they name — the
 * study most recently done, whichever resource kind recorded it. Undefined when nothing was found.
 */
export const latestEvidence = (resources: ContextResources, action: PlanDefinitionAction): Resource | undefined => {
  if (!action.id) return undefined;
  const found = (action.input ?? []).flatMap((_, index) => resources[evidenceRequirementKey(action.id!, index)] ?? []);
  return found
    .filter((r) => resourceInstant(r) !== undefined)
    .sort((a, b) => (resourceInstant(b) ?? '').localeCompare(resourceInstant(a) ?? ''))[0];
};

export const ageYears = (patient: Patient | undefined, now: Date): number | undefined =>
  patient?.birthDate ? differenceInYears(now, parseISO(patient.birthDate)) : undefined;

/**
 * FHIR's Sex Parameter for Clinical Use extension on `Patient`: `0..*`, each instance saying which
 * sex-typical setting to apply, optionally for a period and an intended use. The value is a
 * CodeableConcept from the sex-parameter-for-clinical-use code system.
 */
export const SEX_FOR_CLINICAL_USE_URL = 'http://hl7.org/fhir/StructureDefinition/patient-sexParameterForClinicalUse';
export const SEX_FOR_CLINICAL_USE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/sex-parameter-for-clinical-use';

/** US Core's birth-sex extension on `Patient`, `valueCode` from the Birth Sex value set (F, M, ASKU, OTH, UNK). */
export const US_CORE_BIRTHSEX_URL = 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex';

export type PatientSex = 'female' | 'male' | 'other' | 'unknown';

/** Which field answered: the clinical parameter, the recorded birth sex, or administrative gender. */
export type PatientSexSource = 'sex-for-clinical-use' | 'birth-sex' | 'gender';

/**
 * The sex a protocol should reason with, and where it came from. The source travels with the value
 * because falling back is a clinical assertion, not a null check: reading `Patient.gender` for a
 * screening rule says administrative gender is an acceptable proxy for this patient, which a
 * clinician should be able to see.
 */
export interface PatientSexReading {
  /** Undefined when the source says no single sex-typical setting applies (a `specified` parameter). */
  sex: PatientSex | undefined;
  source: PatientSexSource;
}

/** Verified against the published value set: the codes name a *setting to apply*, not a person's sex. */
const SFCU_TO_SEX: Record<string, PatientSex | undefined> = {
  'female-typical': 'female',
  'male-typical': 'male',
  // No single setting applies uniformly — the instance's supportingInfo or comment says what does.
  // A protocol has no sex to reason with here, and must not fall back to a coarser field to find one.
  specified: undefined,
};

const BIRTHSEX_TO_SEX: Record<string, PatientSex> = { F: 'female', M: 'male', OTH: 'other' };

const isCurrent = (period: Period | undefined, now: Date): boolean =>
  !period?.end || parseISO(period.end).getTime() > now.getTime();

/**
 * The Sex Parameter for Clinical Use instance a screening rule should read, when the chart carries
 * one. The extension is context-scoped, so choosing is a real question: a current instance beats an
 * ended one, and a general one (no `intendedClinicalUse`) beats one scoped to some other purpose,
 * since a screening recommendation names no use of its own. A data-absent `unknown` is no answer and
 * is passed over so a later source can speak.
 */
const sexForClinicalUse = (patient: Patient | undefined, now: Date): PatientSexReading | undefined => {
  const instances = (patient?.extension ?? []).filter((e) => e.url === SEX_FOR_CLINICAL_USE_URL);
  const candidates = instances
    .map((instance) => {
      const sub = (name: string): Extension | undefined => instance.extension?.find((e) => e.url === name);
      const code = sub('value')?.valueCodeableConcept?.coding?.find(
        (c) => c.system === SEX_FOR_CLINICAL_USE_SYSTEM,
      )?.code;
      return {
        code,
        current: isCurrent(sub('period')?.valuePeriod, now),
        general: sub('intendedClinicalUse') === undefined,
      };
    })
    .filter((c) => c.code !== undefined && c.code in SFCU_TO_SEX && c.current)
    .sort((a, b) => Number(b.general) - Number(a.general));
  const chosen = candidates[0];
  return chosen ? { sex: SFCU_TO_SEX[chosen.code!], source: 'sex-for-clinical-use' } : undefined;
};

const birthSex = (patient: Patient | undefined): PatientSexReading | undefined => {
  const code = patient?.extension?.find((e) => e.url === US_CORE_BIRTHSEX_URL)?.valueCode;
  const sex = code ? BIRTHSEX_TO_SEX[code] : undefined;
  // ASKU and UNK are the absence of an answer, not an answer; a later source may still speak.
  return sex ? { sex, source: 'birth-sex' } : undefined;
};

/**
 * The patient's sex for screening applicability, first present wins: the Sex Parameter for Clinical
 * Use (the only field that is *about* clinical decisions, and the one that survives a surgical
 * history), then recorded birth sex (a fixed fact that usually correlates), then administrative
 * gender (documented by FHIR as not for clinical use, and read here only as the proxy of last resort).
 * Nothing upstream records the first two today, so gender is the common path; the order is what makes
 * the answer right once they are recorded. Undefined when no source has anything to say.
 */
export const sexOf = (patient: Patient | undefined, now: Date = new Date()): PatientSexReading | undefined =>
  sexForClinicalUse(patient, now) ??
  birthSex(patient) ??
  (patient?.gender ? { sex: patient.gender, source: 'gender' } : undefined);

/**
 * The active MedicationRequests whose RxNorm coding is in the ValueSet.
 *
 * Compares codes directly, which works because the drug-class ValueSets hold ingredient RxCUIs and
 * a medication arrives carrying its ingredient codings alongside its product one — resolved by the
 * caller before planning, so this stays pure (`withIngredientCodings` in `@liza/services`).
 */
export const medicationRequestsIn = (resources: ContextResources, valueSet: ValueSet): MedicationRequest[] => {
  const rxcuis = new Set(valueSetRxcuis(valueSet));
  return ((resources.activeMedicationRequests as MedicationRequest[] | undefined) ?? []).filter((m) =>
    (m.medicationCodeableConcept?.coding ?? []).some(
      (c) => c.system === MEDICATION_CODE_SYSTEMS.RXNORM && c.code !== undefined && rxcuis.has(c.code),
    ),
  );
};

/** The earliest `authoredOn` among the requests — when the patient started on that class. */
export const earliestAuthoredOn = (requests: MedicationRequest[]): string | undefined =>
  requests
    .map((m) => m.authoredOn)
    .filter((d): d is string => !!d)
    .sort()[0];

/**
 * Active Conditions matching any of the wanted codings. ICD-10-CM matches as a prefix, so `E11.4`
 * covers the category; every other system matches exactly.
 *
 * System-qualified rather than bare prefixes because a diagnosis reaches the chart in whichever
 * system its source used: an imported record frequently carries SNOMED alone, and an ICD-10-only
 * read made a conditional action take the wrong branch on a chart that plainly had the condition
 * (#10129).
 */
export const conditionsMatching = (resources: ContextResources, wanted: readonly Coding[]): Condition[] =>
  ((resources.activeConditions as Condition[] | undefined) ?? []).filter((c) =>
    (c.code?.coding ?? []).some((coding) => wanted.some((w) => codingSatisfies(coding, w))),
  );
